import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { t } from '@lingui/macro';

import Grid from '@decisiv/ui-components/lib/components/Grid';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H4, P } from '@decisiv/ui-components/lib/components/Typography';
import { Item } from '@decisiv/ui-components/lib/atoms/OptionsList/types';
import Envelope from '@decisiv/iconix/lib/components/Envelope';
import Mobile from '@decisiv/iconix/lib/components/Mobile';

import { TextField, Combobox, RadioGroup } from 'components/Form';
import getInputEventValue from 'utils/getInputEventValue';

import type { ContactOption } from './hooks';
import { PrimaryContactContainer, ContactOptionSubtitle } from './styles';

interface PrimaryContactFormProps {
  contacts: ContactOption[];
  loadingContacts: boolean;
  onContactSearchChange: (value: string) => void;
  disabled?: boolean;
}

const RADIO_ITEMS = [
  { label: t`Create New Contact`, value: 'new' },
  { label: t`Select Existing Contact`, value: 'existing' },
];

export default function PrimaryContactForm({
  contacts,
  loadingContacts,
  onContactSearchChange,
  disabled = false,
}: PrimaryContactFormProps) {
  const { watch } = useFormContext();
  const option = watch('primaryContactOption');

  const contactOptions = useMemo(
    () =>
      contacts.map((contact) => ({
        id: contact.id,
        label: `${contact.firstName} ${contact.lastName}`.trim(),
        value: contact.id,
      })),
    [contacts],
  );

  const renderContactOption = (item: Item) => {
    const { id, label } = item as { id?: string; label?: string };
    const contact = contacts.find((c) => c.id === id);
    /* istanbul ignore next -- @preserve: options are derived from `contacts`, so a no-match is defensive only */
    if (!contact) return <P>{label}</P>;
    return (
      <Flex flexDirection="column" justifyContent="center">
        <P>{`${contact.firstName} ${contact.lastName}`.trim()}</P>
        <ContactOptionSubtitle>
          {contact.email && (
            <>
              <Envelope size="small" />
              <P size="small" color="alaskanHusky">
                {contact.email}
              </P>
            </>
          )}
          {contact.email && contact.mobilePhone && (
            <P size="small" color="alaskanHusky">
              •
            </P>
          )}
          {contact.mobilePhone && (
            <>
              <Mobile size="small" />
              <P size="small" color="alaskanHusky">
                {contact.mobilePhone}
              </P>
            </>
          )}
        </ContactOptionSubtitle>
      </Flex>
    );
  };

  return (
    <PrimaryContactContainer marginTop={2}>
      <Grid.Row>
        <Grid.Column>
          <H4>{t`Primary Contact`}</H4>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <RadioGroup
            name="primaryContactOption"
            items={RADIO_ITEMS}
            disabled={disabled}
            size="small"
          />
        </Grid.Column>
      </Grid.Row>

      {option === 'new' ? (
        <>
          <Grid.Row>
            <Grid.Column>
              <TextField
                label={t`First Name`}
                name="primaryContactFirstName"
                required
                disabled={disabled}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <TextField
                label={t`Last Name`}
                name="primaryContactLastName"
                required
                disabled={disabled}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <TextField
                label={t`Email`}
                name="primaryContactEmail"
                required
                disabled={disabled}
              />
            </Grid.Column>
          </Grid.Row>
        </>
      ) : (
        <Grid.Row>
          <Grid.Column>
            <Combobox
              label={t`Contact`}
              name="primaryContactId"
              options={contactOptions}
              variant="async"
              loading={loadingContacts}
              onChangeInputValue={(e) =>
                onContactSearchChange(getInputEventValue(e))
              }
              renderOptionLabel={renderContactOption}
              defaultValue=""
              required
              disabled={disabled}
            />
          </Grid.Column>
        </Grid.Row>
      )}
    </PrimaryContactContainer>
  );
}
